'use client';

import { CSSProperties } from 'react';
import { cn, getColorway } from 'utils/styles';
import { HubSpotProps } from './types';
import { Colorways } from 'types/constants';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useHubSpotForm } from './useHubSpotForm';

const HubSpotFeaturedVariant = ({
  sys,
  sectionName,
  headline,
  formId,
  color = Colorways.Tertiary,
  backgroundImage,
  rightColumnContent,
}: HubSpotProps) => {
  const { iframeRef, tagsUrl } = useHubSpotForm(formId, sys?.id);

  const hasBackgroundImage = backgroundImage?.url != null;

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      style={hasBackgroundImage ? ({ '--bg-image-url': `url(${backgroundImage.url})` } as CSSProperties) : undefined}
      className={cn(
        'relative w-full py-10 lg:py-20',
        hasBackgroundImage && 'bg-(image:--bg-image-url) bg-center bg-cover',
        !hasBackgroundImage && 'darkest-green-colorway',
      )}>
      {hasBackgroundImage && <div className="absolute inset-0 bg-[rgba(5,35,22,0.85)]" />}

      <div className="content-wrapper">
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start bg-bg-primary rounded-lg overflow-hidden">
          {/* Left column: Headline + Form */}
          <div className="p-6 py-12 flex flex-col gap-6 justify-center">
            {headline && (
              <div
                className="font-sans font-semibold text-heading-lg-fixed md:text-heading-xxl text-content-primary"
                {...liveUpdateAttrs(sys?.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <div {...liveUpdateAttrs(sys?.id, 'formId')}>
              {tagsUrl && (
                <iframe
                  ref={iframeRef}
                  title="HubSpot"
                  id={`hubspot-iframe-${sys?.id}`}
                  src={tagsUrl}
                  sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
                  style={{
                    width: '100%',
                    height: '500px',
                    border: 0,
                    scrollMargin: '100px',
                  }}
                  data-testid="hubspotFormContainer"
                />
              )}
            </div>
          </div>

          {/* Right column: Colored panel with rich text content */}
          {rightColumnContent && (
            <div
              className={cn(getColorway(color), 'p-12 flex flex-col items-center h-full gap-6')}
              {...liveUpdateAttrs(sys?.id, 'rightColumnContent')}>
              <RichTextCopy copy={rightColumnContent} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HubSpotFeaturedVariant;
